// 产品矩阵系统 - 产品状态管理
import { create } from 'zustand';
import type { Product, ProductModel, ProductCategory, ProductFilter } from '../types';
import * as productService from '../services/product-service';

interface ProductState {
  // 状态
  allProducts: Product[];  // 所有产品（不受筛选影响）
  products: Product[];     // 筛选后的产品列表
  filteredProducts: Product[];
  favoriteProducts: Product[];
  selectedProduct: Product | null;
  isLoading: boolean;
  error: string | null;
  
  // 筛选条件
  filter: ProductFilter;
  
  // 分页
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  
  // 分类统计
  categoryStats: Record<ProductCategory | 'all', number>;
  
  // Actions
  fetchProducts: (resetPage?: boolean) => Promise<void>;
  fetchFavorites: () => Promise<void>;
  fetchCategoryStats: () => Promise<void>;
  
  setFilter: (filter: Partial<ProductFilter>) => void;
  resetFilter: () => void;
  setPage: (page: number) => void;
  
  selectProduct: (product: Product | null) => void;
  
  // CRUD
  createProduct: (product: Partial<Product>) => Promise<Product>;
  updateProduct: (id: string, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  
  // 型号管理
  createProductModel: (model: Partial<ProductModel>) => Promise<ProductModel>;
  updateProductModel: (id: string, updates: Partial<ProductModel>) => Promise<void>;
  deleteProductModel: (id: string) => Promise<void>;
  
  // 收藏
  toggleFavorite: (productId: string) => Promise<void>;
  isFavorite: (productId: string) => boolean;
  
  // 工具方法
  getProductById: (id: string) => Product | undefined;
  clearError: () => void;
}

const defaultFilter: ProductFilter = {
  category: 'all',
  search: '',
  processType: undefined,
  isFavorite: false,
};

export const useProductStore = create<ProductState>((set, get) => ({
  // 初始状态
  allProducts: [],      // 所有产品缓存
  products: [],
  filteredProducts: [],
  favoriteProducts: [],
  selectedProduct: null,
  isLoading: false,
  error: null,
  filter: { ...defaultFilter },
  page: 1,
  pageSize: 50,
  total: 0,
  hasMore: false,
  categoryStats: {
    all: 0,
    all_in_one: 0,
    product: 0,
    module: 0,
  },

  // 获取产品列表
  fetchProducts: async (resetPage = false) => {
    const state = get();
    const page = resetPage ? 1 : state.page;
    
    set({ isLoading: true, error: null });
    
    try {
      const result = await productService.fetchProducts(
        state.filter,
        page,
        state.pageSize
      );
      
      // 判断是否是获取全部产品（无筛选条件）
      const isAllProducts = state.filter.category === 'all' && 
                           !state.filter.search && 
                           !state.filter.processType;
      
      set({
        products: result.data,
        filteredProducts: result.data,
        // 如果是获取全部产品，同时更新 allProducts 缓存
        ...(isAllProducts ? { allProducts: result.data } : {}),
        total: result.total,
        hasMore: result.hasMore,
        page,
        isLoading: false,
      });
      
      // 如果 allProducts 为空，先获取一次全部产品
      if (state.allProducts.length === 0 && !isAllProducts) {
        const allResult = await productService.fetchProducts(
          { ...defaultFilter },
          1,
          100
        );
        set({ allProducts: allResult.data });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      set({
        error: (error as Error).message,
        isLoading: false,
      });
    }
  },

  // 获取收藏产品
  fetchFavorites: async () => {
    try {
      const favorites = await productService.fetchFavoriteProducts();
      set({ favoriteProducts: favorites });
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  },

  // 获取分类统计
  fetchCategoryStats: async () => {
    try {
      const stats = await productService.getProductCategoryStats();
      set({ categoryStats: stats });
    } catch (error) {
      console.error('Error fetching category stats:', error);
    }
  },

  // 设置筛选条件
  setFilter: (newFilter) => {
    set((state) => ({
      filter: { ...state.filter, ...newFilter },
    }));
    // 筛选条件变化时重新获取数据
    get().fetchProducts(true);
  },

  // 重置筛选
  resetFilter: () => {
    set({ filter: { ...defaultFilter } });
    get().fetchProducts(true);
  },

  // 设置页码
  setPage: (page) => {
    set({ page });
    get().fetchProducts();
  },

  // 选择产品
  selectProduct: (product) => {
    set({ selectedProduct: product });
  },

  // 创建产品 - 乐观更新模式
  createProduct: async (product) => {
    // 生成临时 ID 用于乐观更新
    const tempId = `temp-${Date.now()}`;
    const tempProduct: Product = {
      id: tempId,
      name: product.name || '',
      name_en: product.name_en || '',
      category: product.category || 'product',
      series_name: product.series_name || '',
      image_url: product.image_url || '',
      pitch_range: product.pitch_range || '',
      size_specs: product.size_specs || '',
      process_type: product.process_type || '',
      features: product.features || [],
      slogan: product.slogan || '',
      is_featured: product.is_featured || false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      models: [],
    } as Product;
    
    // 乐观更新：立即添加到列表
    set((state) => ({
      products: [tempProduct, ...state.products],
      allProducts: [tempProduct, ...state.allProducts],
    }));
    
    try {
      const newProduct = await productService.createProduct(product);
      
      // 替换临时产品为真实产品
      set((state) => ({
        products: state.products.map((p) => 
          p.id === tempId ? newProduct : p
        ),
        allProducts: state.allProducts.map((p) => 
          p.id === tempId ? newProduct : p
        ),
      }));
      
      // 后台静默更新分类统计（不影响 UI）
      get().fetchCategoryStats();
      
      return newProduct;
    } catch (error) {
      // 回滚：移除临时产品
      set((state) => ({
        products: state.products.filter((p) => p.id !== tempId),
        allProducts: state.allProducts.filter((p) => p.id !== tempId),
        error: (error as Error).message,
      }));
      console.error('Error creating product:', error);
      throw error;
    }
  },

  // 更新产品 - 乐观更新模式
  updateProduct: async (id, updates) => {
    // 保存旧状态用于回滚
    const state = get();
    const oldProduct = state.products.find((p) => p.id === id);
    
    // 乐观更新：立即更新本地状态
    set((state) => ({
      products: state.products.map((p) =>
        p.id === id ? { ...p, ...updates, updated_at: new Date().toISOString() } : p
      ),
      allProducts: state.allProducts.map((p) =>
        p.id === id ? { ...p, ...updates, updated_at: new Date().toISOString() } : p
      ),
      filteredProducts: state.filteredProducts.map((p) =>
        p.id === id ? { ...p, ...updates, updated_at: new Date().toISOString() } : p
      ),
      selectedProduct:
        state.selectedProduct?.id === id
          ? { ...state.selectedProduct, ...updates }
          : state.selectedProduct,
    }));
    
    try {
      await productService.updateProduct(id, updates);
    } catch (error) {
      // 回滚到旧状态
      if (oldProduct) {
        set((state) => ({
          products: state.products.map((p) =>
            p.id === id ? oldProduct : p
          ),
          allProducts: state.allProducts.map((p) =>
            p.id === id ? oldProduct : p
          ),
          error: (error as Error).message,
        }));
      }
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // 删除产品 - 乐观更新模式
  deleteProduct: async (id) => {
    // 保存旧状态用于回滚
    const state = get();
    const deletedProduct = state.products.find((p) => p.id === id);
    const deletedIndex = state.products.findIndex((p) => p.id === id);
    
    // 乐观更新：立即从本地状态移除
    set((state) => ({
      products: state.products.filter((p) => p.id !== id),
      allProducts: state.allProducts.filter((p) => p.id !== id),
      filteredProducts: state.filteredProducts.filter((p) => p.id !== id),
      selectedProduct:
        state.selectedProduct?.id === id ? null : state.selectedProduct,
    }));
    
    try {
      await productService.deleteProduct(id);
      // 后台静默更新分类统计
      get().fetchCategoryStats();
    } catch (error) {
      // 回滚：恢复删除的产品
      if (deletedProduct) {
        set((state) => {
          const products = [...state.products];
          products.splice(deletedIndex, 0, deletedProduct);
          const allProducts = [...state.allProducts];
          const allIndex = state.allProducts.findIndex((p) => p.id === id);
          if (allIndex === -1) allProducts.splice(deletedIndex, 0, deletedProduct);
          return {
            products,
            allProducts,
            error: (error as Error).message,
          };
        });
      }
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  // 创建产品型号
  createProductModel: async (model) => {
    try {
      const newModel = await productService.createProductModel(model);
      
      // 更新本地产品的型号列表
      set((state) => ({
        products: state.products.map((p) =>
          p.id === model.product_id
            ? { ...p, models: [...(p.models || []), newModel] }
            : p
        ),
      }));
      
      return newModel;
    } catch (error) {
      console.error('Error creating product model:', error);
      throw error;
    }
  },

  // 更新产品型号
  updateProductModel: async (id, updates) => {
    try {
      await productService.updateProductModel(id, updates);
      
      // 更新本地状态
      set((state) => ({
        products: state.products.map((p) => ({
          ...p,
          models: p.models?.map((m) =>
            m.id === id ? { ...m, ...updates } : m
          ),
        })),
      }));
    } catch (error) {
      console.error('Error updating product model:', error);
      throw error;
    }
  },

  // 删除产品型号
  deleteProductModel: async (id) => {
    try {
      await productService.deleteProductModel(id);
      
      // 从本地状态移除
      set((state) => ({
        products: state.products.map((p) => ({
          ...p,
          models: p.models?.filter((m) => m.id !== id),
        })),
      }));
    } catch (error) {
      console.error('Error deleting product model:', error);
      throw error;
    }
  },

  // 切换收藏状态
  toggleFavorite: async (productId) => {
    const state = get();
    const isFav = state.favoriteProducts.some((p) => p.id === productId);
    
    try {
      if (isFav) {
        await productService.removeProductFavorite(productId);
        set({
          favoriteProducts: state.favoriteProducts.filter(
            (p) => p.id !== productId
          ),
        });
      } else {
        await productService.addProductFavorite(productId);
        const product = state.products.find((p) => p.id === productId);
        if (product) {
          set({
            favoriteProducts: [...state.favoriteProducts, product],
          });
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  },

  // 检查是否收藏
  isFavorite: (productId) => {
    return get().favoriteProducts.some((p) => p.id === productId);
  },

  // 根据ID获取产品（从所有产品缓存中查找，不受筛选影响）
  getProductById: (id) => {
    const state = get();
    // 优先从 allProducts 查找，确保画布中的产品不受筛选影响
    return state.allProducts.find((p) => p.id === id) || 
           state.products.find((p) => p.id === id);
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },
}));
